import * as React from "react";
import { IoCloseSharp } from "react-icons/io5";
import { useSelector } from "react-redux";
import { Accordion } from "react-accessible-accordion";
import PulseLoader from "react-spinners/PulseLoader";
import { useNavigate } from "react-router";
import { confirmAlert } from "react-confirm-alert";
import { Money } from "@utils/currency";
import { OrderProduct, OrderStatus } from "@utils/types";
import OrderProductListItem from "@components/OrderProductListItem";
import { RootState, useAppDispatch } from "@services/Redux";
import Styled from "./styled";
import { fetchOrder } from "@services/Redux/order";
import Api from "@services/Api";
import { generateWhastappText, getOrderStatusDescription, isSellerModeOn, toastError, toastMessage } from "@utils/functions";
import EmptyCart from "@components/EmptyCart";
import { BsWhatsapp } from "react-icons/bs";
import ReactGA from "react-ga4";
import MetaPixel from "@utils/meta/MetaPixel";

const OrderScreen: React.FC = () => {
  const { order, company } = useSelector((state: RootState) => state);
  const seller = useSelector((state: RootState) => state.company.seller);
  const totalValue = Money(order.data?.value ?? 0).add(order.data?.shipment_value ?? 0).value;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const totalItems = order.data?.items?.reduce((acc, item) => {
    return acc + (item.deleted_at ? 0 : item.quantity);
  }, 0);

  const getProductsWithItems = () => {
    if (!order.data) return [];
    const products = [] as OrderProduct[];
    const items = order.data?.items;
    if (!items) return [];
    for (const item of items) {
      if (item.deleted_at) continue;
      let product = products.find((p) => p.id === item.product.id);
      if (product) {
        product.items.push(item);
      } else {
        product = { ...item.product, items: [item] };
        products.push(product);
      }
    }
    return products;
  };

  const clearOrder = async () => {
    try {
      if (order.data?.status === OrderStatus.CREATED) {
        await Api.post(`/order/${order.data.id}/cancel`);
      }
      ReactGA.event({ action: "clear_order", category: "Order", label: "Usuário limpou o pedido" });
      MetaPixel.trackCustom("clear_order", { category: "Order", label: "Usuário limpou o pedido" });
      localStorage.removeItem(`company_${company.data.code}_order_code`);
      dispatch(fetchOrder());
      navigate(`/`, { replace: true });
    } catch (error) {
      console.log(error, "error on clearOrder");
      toastError(error);
    }
  };

  const goToCheckout = () => {
    const itemsNumber = order.data?.items?.reduce((acc, item) => acc + item.quantity, 0) ?? 0;
    const orderValue = order.data?.value ?? 0;
    if (company.data.minimun_order_value > orderValue) {
      return toastMessage(`O valor mínimo do pedido é ${Money(company.data.minimun_order_value).format()}`);
    }
    if (company.data.minimun_pieces_number > itemsNumber) {
      return toastMessage(`O número mínimo de itens é ${company.data.minimun_pieces_number}`);
    }
    if (order.data) {
      ReactGA.gtag("event", "begin_checkout", {
        value: order.data.value,
        currency: "BRL",
        items: order.data?.items.map((item) => ({
          item_id: item.product?.id,
          item_name: item.product?.name,
          price: item.price,
          item_category: item.product?.categories?.[0]?.name,
          quantity: item.quantity,
        })),
      });
      MetaPixel.trackCustom("begin_checkout", {
        value: order.data.value,
        currency: "BRL",
        items: order.data?.items.map((item) => ({
          item_id: item.product?.id,
          item_name: item.product?.name,
          price: item.price,
          item_category: item.product?.categories?.[0]?.name,
          quantity: item.quantity,
        })),
      });
    }
    navigate(`/checkout`);
  };

  const handleSendOrderToSeller = () => {
    /*
      Here we know that order.data.customer_name is defined because the button
      that triggers this method is only visible when the order is finished.
    */
    const sellerName = localStorage.getItem(`company_${company.data?.code}_seller_name`);
    const wppText = generateWhastappText(order.data?.customer_name ?? "", sellerName ?? undefined);
    const phone = `55${(seller?.phone ?? company.data.phone).replace(/\D/g, "")}`;
    window.open(`https://api.whatsapp.com/send?phone=${phone}&text=${wppText}`, "_blank")?.focus();
  };

  const handleClearOrderButtonClick = () => {
    confirmAlert({
      title: "Atenção",
      message: "Tem certeza que deseja iniciar um novo pedido?",
      buttons: [
        {
          label: "Sim",
          onClick: () => clearOrder(),
        },
        {
          label: "Cancelar",
          onClick: () => {},
        },
      ],
    });
  };

  const handleContactCompanyButtonClick = () => {
    const phone = `55${(seller?.phone ?? company.data.phone).replace(/\D/g, "")}`;
    window.open(`https://api.whatsapp.com/send?phone=${phone}`, "_blank")?.focus();
  };

  if (order.loadingOrder) return <></>;
  if (!order.data || order.data.value <= 0) return <EmptyCart />;

  return (
    <div>
      <Styled.RootContainer>
        <Styled.HeaderContainer className={"border-bottom mb-3"}>
          <Styled.HeaderCloseButtonContainer
            onClick={() => {
              if (window.history.state && window.history.state.idx > 0) {
                navigate(-1);
              } else {
                navigate("/", { replace: true });
              }
            }}
          >
            <IoCloseSharp size={26} color={"black"} />
          </Styled.HeaderCloseButtonContainer>
          <Styled.HeaderTitle>Sacola</Styled.HeaderTitle>
        </Styled.HeaderContainer>
        {order.data.status !== OrderStatus.CREATED && !isSellerModeOn() && (
          <div className={"d-flex justify-content-center mb-3 text-center"}>
            <span>{getOrderStatusDescription(order.data.status)}</span>
          </div>
        )}
        <Styled.SummaryContainer className={"row m-2 py-2"}>
          <div className={"col-6 border-end"}>
            <span style={{ fontSize: 12 }}>Total de peças</span>
            <span style={{ fontSize: 16 }}>
              {order.loadingAddProductRequest || order.loadingRemoveProductFromOrder ? <PulseLoader color="black" size={8} /> : <b>{totalItems ?? 0}</b>}
            </span>
          </div>
          <div className={"col-6"}>
            <span style={{ fontSize: 12 }}>Total à pagar</span>
            <span style={{ fontSize: 16 }}>
              {order.loadingAddProductRequest || order.loadingRemoveProductFromOrder ? (
                <PulseLoader color="black" size={8} />
              ) : (
                <div>
                  <b>
                    {Money(totalValue ?? 0)
                      .subtract(order.data.discounts_value ?? 0)
                      .format()}
                  </b>
                  {order.data.discounts_value ? <span style={{ fontSize: 10 }}>Desconto de {Money(order.data.discounts_value).format()} aplicado</span> : null}
                  {order.data.shipment_value ? <span style={{ fontSize: 10 }}>Envio: {Money(order.data.shipment_value).format()}</span> : <></>}
                </div>
              )}
            </span>
          </div>
        </Styled.SummaryContainer>
        <Accordion allowZeroExpanded={true}>
          <Styled.ProductsContainer className={"row m-2 py-2"}>
            {getProductsWithItems().map((item) => (
              <OrderProductListItem key={item.id} product={item} />
            ))}
          </Styled.ProductsContainer>
        </Accordion>
        <div className={"row m-2"}>
          {company.data.minimun_pieces_number > 0 && (
            <div className={"col text-center mb-4 text-danger"}>Quantidade mínima de peças: {company.data.minimun_pieces_number}</div>
          )}
          {company.data.minimun_order_value > 0 && (
            <div className={"col text-center mb-4 text-danger"}>Valor mínimo do pedido: {Money(company.data.minimun_order_value).format()}</div>
          )}
          {order.data.status === OrderStatus.CREATED && !isSellerModeOn() && (
            <div className={"col-12 p-0 mb-2"}>
              <Styled.SendOrderButton onClick={goToCheckout}>
                <span>Finalizar compra</span>
              </Styled.SendOrderButton>
            </div>
          )}
          {order.data.status === OrderStatus.READY_TO_PAY && company.data.is_payment_enabled && order.data.emepag_order_code && !isSellerModeOn() && (
            <div className={"col-12 p-0 mb-2"}>
              <a href={`https://link.emepag.com.br/${order.data?.emepag_order_code}`} target="_blank" rel="noreferrer">
                <Styled.PayButton>
                  <span>Efetuar pagamento</span>
                </Styled.PayButton>
              </a>
            </div>
          )}
          {order.data.status === OrderStatus.WAITING_SELLER_CONFIRMATION && !isSellerModeOn() && (
            <div className={"col-12 p-0 mb-2"}>
              <Styled.SendOrderToSellerButton onClick={handleSendOrderToSeller}>
                <span>Enviar pedido ao vendedor</span>
                <BsWhatsapp className="ms-2" color="white" size={20} />
              </Styled.SendOrderToSellerButton>
            </div>
          )}
          {order.data.status !== OrderStatus.CREATED && (
            <div className={"col-12 p-0 mb-2"}>
              <Styled.CancelOrderButton onClick={handleClearOrderButtonClick}>
                <span>Iniciar um novo pedido</span>
              </Styled.CancelOrderButton>
            </div>
          )}
          {!isSellerModeOn() && (
            <div className={"col-12 p-0 mb-2"}>
              <Styled.ContactCompanyButton onClick={handleContactCompanyButtonClick}>
                <span>Entrar em contato com a marca</span>
              </Styled.ContactCompanyButton>
            </div>
          )}
        </div>
      </Styled.RootContainer>
    </div>
  );
};

export default OrderScreen;
